import React from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const Header = styled.div`
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
  padding: 4rem 1rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0rem 1rem 4rem;
  color: ${({ theme }) => theme.colors.black};
`;

const Section = styled.section`
  margin-bottom: 0rem;
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 1.5rem;
`;

const LastUpdated = styled.p`
  color: ${({ theme }) => theme.colors.darkGray};
  font-style: italic;
  margin-bottom: 2rem;
`;

const Paragraph = styled.p`
  margin-bottom: 0rem;
  line-height: 1.6;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 2rem;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const PrivacyPolicyPage: React.FC = () => {
  const pageTitle = 'Privacy Policy | ElectroCable';
  const pageDescription = 'Understand how ElectroCable collects, uses, and protects your personal information when you use our website and services. We are committed to safeguarding your privacy.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const policyPageUrl = `${siteUrl}/privacy-policy`;
  const ogImageUrl = `${siteUrl}/images/logo.svg`; // Or a generic legal/company OG image

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={policyPageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* For legal documents, article:published_time could be relevant if you want to specify */}
        {/* <meta property="article:published_time" content="2025-04-02T00:00:00Z" /> */}
        {/* <meta property="article:modified_time" content="2025-04-02T00:00:00Z" /> */}
        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={policyPageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />
      </Helmet>

      <Header>
        <HeaderTitle>Privacy Policy</HeaderTitle>
      </Header>

      <Container>
        <LastUpdated>Last Updated: April 2, 2025</LastUpdated>

        <Section>
          <Paragraph>
            At ElectroCable, we respect your privacy and are committed to protecting your personal data.
            This privacy policy will inform you about how we look after your personal data when you visit
            our website and tell you about your privacy rights and how the law protects you.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Information We Collect</SectionTitle>
          <Paragraph>
            We may collect, use, store, and transfer different kinds of personal data about you which we have grouped together as follows:
          </Paragraph>
          <List>
            <li><strong>Identity Data</strong> includes first name, last name, username, or similar identifier.</li>
            <li><strong>Contact Data</strong> includes billing address, delivery address, email address, and telephone numbers.</li>
            <li><strong>Technical Data</strong> includes internet protocol (IP) address, your login data, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access this website.</li>
            <li><strong>Usage Data</strong> includes information about how you use our website, products, and services.</li>
            <li><strong>Marketing and Communications Data</strong> includes your preferences in receiving marketing from us and our third parties and your communication preferences.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <Paragraph>
            We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
          </Paragraph>
          <List>
            <li>Where we need to perform the contract we are about to enter into or have entered into with you.</li>
            <li>Where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests.</li>
            <li>Where we need to comply with a legal obligation.</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Data Security</SectionTitle>
          <Paragraph>
            We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorized way, altered, or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors, and other third parties who have a business need to know.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Cookies</SectionTitle>
          <Paragraph>
            Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to the owners of the site. We use cookies to enhance your experience on our website and to help us understand how our website is used.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Your Legal Rights</SectionTitle>
          <Paragraph>
            Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:
          </Paragraph>
          <List>
            <li>Request access to your personal data</li>
            <li>Request correction of your personal data</li>
            <li>Request erasure of your personal data</li>
            <li>Object to processing of your personal data</li>
            <li>Request restriction of processing your personal data</li>
            <li>Request transfer of your personal data</li>
            <li>Right to withdraw consent</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>6. Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about this privacy policy or our privacy practices, please contact us at:
          </Paragraph>
          <Paragraph>
            <strong>Email:</strong> info@electrocableus.com<br />
            <strong>Phone:</strong> +1 847 376 9187<br />
            <strong>Address:</strong> 10408 West Mcnab Road Tamarac, FL 33321, United States
          </Paragraph>
        </Section>
      </Container>
    </>
  );
};

export default PrivacyPolicyPage; 